import { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { t } from 'localization'
import { RaffleDetail } from 'shared/types/raffles'
import { FC_STORES } from 'shared/constants/raffleStores'
import { colors } from 'shared/lib'
import { formatRaffleDateTime, formatSizeLabel } from './raffleUtils'

const TP = 'shared.components.Raffles.RaffleEntryStoreStep'

type RaffleEntryStoreStepProps = {
  raffle: RaffleDetail
  selectedStore: string | null
  selectedSize: string | null
  onStoreSelect: (store: string) => void
  onSizeSelect: (size: string) => void
  onContinue: () => void
}

export const RaffleEntryStoreStep = ({
  raffle,
  selectedStore,
  selectedSize,
  onStoreSelect,
  onSizeSelect,
  onContinue,
}: RaffleEntryStoreStepProps) => {
  const availableStores = useMemo(
    () =>
      raffle.stores
        .map((city) => ({ city, ...FC_STORES[city] }))
        .filter((store) => store.id),
    [raffle.stores],
  )

  useEffect(() => {
    if (availableStores.length === 1 && !selectedStore) {
      onStoreSelect(availableStores[0].city)
    }
  }, [availableStores, selectedStore, onStoreSelect])

  const isReady = selectedStore !== null && selectedSize !== null

  return (
    <Wrapper data-qa="raffle-entry-store-step">
      <ScrollContent>
        <ProductSummary>
          <ProductThumb src={raffle.mainImage.src} alt={raffle.productName} />
          <ProductInfo>
            <ProductName>{raffle.productName}</ProductName>
            <ProductPrice>
              {t(`${TP}.rafflePrice`, 'Price')}: ${raffle.rafflePrice}
            </ProductPrice>
            <ProductCloses>
              Closes: {formatRaffleDateTime(raffle.endDate)}
            </ProductCloses>
          </ProductInfo>
        </ProductSummary>

        <Divider />

        <Section>
          <SectionHeader>
            <SectionTitle>{t(`${TP}.pickupStore`, 'PICKUP LOCATION')}</SectionTitle>
            <StoreNote>
              {t(
                `${TP}.storeNote`,
                'Selected entrants must pick up their item in store.',
              )}
            </StoreNote>
          </SectionHeader>
          <StoreList>
            {availableStores.map((store) => (
              <StoreCard
                key={store.id}
                $selected={selectedStore === store.city}
                onClick={() => onStoreSelect(store.city)}
                aria-pressed={selectedStore === store.city}
                data-qa={`store-option-${store.id}`}
              >
                <StoreName>{store.name}</StoreName>
                <StoreAddress>{store.address}</StoreAddress>
              </StoreCard>
            ))}
          </StoreList>
        </Section>

        <Divider />

        <Section>
          <SectionTitle>Select {formatSizeLabel(raffle.sizeUnit, raffle.gender)} Size</SectionTitle>
          <SizeGrid data-qa="raffle-entry-size-grid">
            {raffle.availableSizes.map((size) => (
              <SizePill
                key={size}
                $selected={selectedSize === size}
                onClick={() => onSizeSelect(size)}
                aria-pressed={selectedSize === size}
                data-qa={`size-option-${size}`}
              >
                {size}
              </SizePill>
            ))}
          </SizeGrid>
        </Section>
      </ScrollContent>

      <Footer>
        <ContinueButton
          disabled={!isReady}
          onClick={onContinue}
          data-qa="raffle-entry-continue"
        >
          {t(`${TP}.continue`, 'CONTINUE')}
        </ContinueButton>
      </Footer>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;
`

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: 0;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
`

const SectionTitle = styled.h3`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 12px;
  text-transform: uppercase;
  color: ${colors.FC2_BLACK};
  margin: 0;
`

const StoreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StoreCard = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  min-height: 64px;
  padding: 16px;
  border-radius: 2px;
  background-color: ${({ $selected }) => ($selected ? colors.FC2_WHITE : colors.FC2_OFF_WHITE)};
  border: 1.5px solid ${({ $selected }) => ($selected ? colors.FC2_BLACK : 'transparent')};
  box-shadow: ${({ $selected }) =>
    $selected ? 'none' : `0 0 0 0.5px ${colors.FC2_DISABLED_GREY}`};
  cursor: pointer;
  text-align: left;
  transition: background-color 100ms ease, box-shadow 100ms ease, border-color 100ms ease;
`

const StoreName = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: ${colors.FC2_BLACK};
`

const StoreAddress = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: ${colors.FC2_GREY};
`

const StoreNote = styled.p`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: ${colors.FC2_BLACK};
  margin: 24px 0 0;
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${colors.FC2_LIGHTEST_GREY};
  margin: 0;
`

const SizeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
`

const SizePill = styled.button<{ $selected: boolean }>`
  height: 40px;
  border-radius: 2px;
  background-color: ${({ $selected }) => ($selected ? colors.FC2_WHITE : colors.FC2_OFF_WHITE)};
  border: 1.5px solid ${({ $selected }) => ($selected ? colors.FC2_BLACK : 'transparent')};
  box-shadow: ${({ $selected }) =>
    $selected ? 'none' : `0 0 0 0.5px ${colors.FC2_DISABLED_GREY}`};
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: ${({ $selected }) => ($selected ? 500 : 400)};
  font-size: 12px;
  color: ${colors.FC2_BLACK};
  cursor: pointer;
  transition: background-color 100ms ease, box-shadow 100ms ease, border-color 100ms ease, font-weight 100ms ease;
`

const Footer = styled.div`
  border-top: 1px solid ${colors.FC2_LIGHTEST_GREY};
  padding: 16px 24px 24px;
  flex-shrink: 0;
  background: ${colors.FC2_WHITE};
`

const ProductSummary = styled.div`
  display: flex;
  gap: 12px;
  padding: 0;
`

const ProductThumb = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  flex-shrink: 0;
`

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-width: 0;
`

const ProductName = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: ${colors.FC2_BLACK};
`

const ProductPrice = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: ${colors.FC2_GREY};
`

const ProductCloses = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: ${colors.FC2_LIGHT_GREY};
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`

const ContinueButton = styled.button`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.02em;
  width: 100%;
  height: 56px;
  background-color: ${colors.FC2_BLACK};
  color: ${colors.FC2_WHITE};
  border: none;
  border-radius: 2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 150ms ease;

  &:disabled {
    background-color: ${colors.FC2_DISABLED_GREY};
    pointer-events: none;
  }
`
